import React, { useEffect, useMemo, useRef, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { Plus, Trash, Lock, LockOpen, Camera, CheckCircle } from "@phosphor-icons/react";

const FRAMING = ["framing_making", "structure_making"];
const EDITABLE = [...FRAMING, "design_making", "design_pending"];
const snap = (v, step) => (v <= 0 ? 0 : Math.ceil(v / step - 1e-9) * step);
const marginFor = (name) => (/SS/i.test(name || "") ? 6 : 3);
const rowSqft = (r, name) => {
  const m = marginFor(name), w = Number(r.width_in) || 0, h = Number(r.height_in) || 0;
  return ((r.round_up ? snap(w, m) : w) * (r.round_up ? snap(h, m) : h) * (Number(r.quantity) || 0)) / 144;
};
const n3 = (v) => Number(v || 0).toLocaleString("en-IN", { maximumFractionDigits: 3 });
const blank = (item_id = "") => ({ item_id, width_in: "", height_in: "", round_up: true, quantity: 1 });

// mode: "admin" | "fab". Same section on order detail and fabricator phone view; never shows money.
export default function CuttingPlatesSection({ order, mode, onChanged, canUnlock }) {
  const base = mode === "fab" ? `/fabrication/orders/${order.id}` : `/orders/${order.id}`;
  const status = order.raw_status || order.status;
  const [items, setItems] = useState([]);
  const [rows, setRows] = useState([]);
  const [busy, setBusy] = useState(false);
  const ref = useRef(null);
  const locked = !EDITABLE.includes(status) && !order.plates_unlocked;
  const notOpen = status === "new_order";
  const byId = useMemo(() => Object.fromEntries(items.map((i) => [i.id, i])), [items]);
  const total = rows.reduce((s, r) => s + rowSqft(r, byId[r.item_id]?.name), 0);

  useEffect(() => { api.get("/plate-items").then(({ data }) => setItems(data || [])).catch(() => {}); }, []);
  useEffect(() => {
    setRows((order.cutting_plates || []).map((p) => ({ item_id: p.item_id, width_in: p.width_in_raw ?? p.width_in,
      height_in: p.height_in_raw ?? p.height_in, round_up: !!p.round_up, quantity: p.quantity })));
  }, [order.id, order.cutting_plates]);
  useEffect(() => {
    if (FRAMING.includes(status) && !(order.cutting_plates || []).length) ref.current?.scrollIntoView({ behavior: "smooth", block: "center" });
  }, [order.id, status]); // eslint-disable-line react-hooks/exhaustive-deps

  const setRow = (i, k, v) => setRows((rs) => rs.map((r, j) => (j === i ? { ...r, [k]: v } : r)));
  const payload = () => ({ plates: rows.map((r) => ({ ...r, width_in: Number(r.width_in), height_in: Number(r.height_in), quantity: Number(r.quantity) })) });
  const call = async (fn, ok) => {
    setBusy(true);
    try { await fn(); toast.success(ok); onChanged?.(); } catch (e) { toast.error(formatApiError(e.response?.data?.detail) || e.message); }
    finally { setBusy(false); }
  };
  const save = () => call(() => api.put(`${base}/cutting-plates`, payload()), "Cutting sizes saved");
  const framingDone = () => {
    if (!rows.length) { toast.error("Kam se kam ek cutting size daaliye"); return; }
    if (!window.confirm(`FRAMING DONE? ${n3(total)} sqft plate stock se katega aur order DESIGN MAKING me jayega.`)) return;
    call(async () => { await api.put(`${base}/cutting-plates`, payload()); await api.post(`${base}/framing-done`); }, "Framing done → Design Making");
  };
  const unlock = () => window.confirm("Cutting sizes UNLOCK karein? Change karne pe stock difference se adjust hoga.")
    && call(() => api.post(`/orders/${order.id}/cutting-plates/unlock`), "Unlocked — ek baar edit kar sakte ho");

  return (
    <div ref={ref} className="surface p-4 rounded-sm border-l-4 border-cyan-500 space-y-3" data-testid="cutting-plates-section">
      <div className="flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="text-[10px] font-mono-num tracking-widest text-cyan-300">// CUTTING SHEET SIZES — uploaded after framing</div>
          <div className="text-xs text-slate-400">Design team aur operator yahi size use karenge (gate size nahi).</div>
        </div>
        {locked && !notOpen && (
          <span className="flex items-center gap-1 text-[10px] font-mono-num text-amber-300" data-testid="plates-locked-badge"><Lock size={12} /> LOCKED</span>
        )}
        {locked && !notOpen && canUnlock && (
          <button onClick={unlock} disabled={busy} className="h-8 px-3 border border-amber-500 text-amber-200 rounded-sm text-[10px] font-mono-num flex items-center gap-1" data-testid="plates-unlock-btn"><LockOpen size={12} /> UNLOCK</button>
        )}
      </div>
      {notOpen ? (
        <div className="text-sm text-slate-400 italic flex items-center gap-2" data-testid="plates-not-open"><Lock size={14} /> Opens when framing starts</div>
      ) : (
        <>
          <PlateRows rows={rows} items={items} byId={byId} locked={locked} setRow={setRow}
            onDelete={(i) => setRows((rs) => rs.filter((_, j) => j !== i))} />
          <div className="flex items-center gap-2 flex-wrap">
            {!locked && (
              <button onClick={() => setRows((rs) => [...rs, blank(rs[rs.length - 1]?.item_id || "")])} className="h-9 px-3 border border-cyan-500/60 text-cyan-200 rounded-sm text-[11px] font-mono-num flex items-center gap-1" data-testid="plates-add-row"><Plus size={12} /> ADD SHEET</button>
            )}
            <span className="ml-auto text-sm text-slate-300">Total plate: <b className="font-mono-num text-cyan-300" data-testid="plates-total-sqft">{n3(total)} sqft</b></span>
          </div>
          {!locked && (
            <div className="flex gap-2 flex-wrap">
              <button onClick={save} disabled={busy} className="h-10 px-4 rounded-sm border border-slate-500 text-slate-200 text-xs font-mono-num tracking-widest" data-testid="plates-save-btn">SAVE SIZES</button>
              {FRAMING.includes(status) && (
                <button onClick={framingDone} disabled={busy || !rows.length} className="h-10 px-4 rounded-sm bg-emerald-500 hover:bg-emerald-600 text-white text-xs font-heading tracking-widest flex items-center gap-1 disabled:opacity-50" data-testid="plates-framing-done-btn"><CheckCircle size={16} weight="fill" /> FRAMING DONE · SEND TO DESIGN</button>
              )}
            </div>
          )}
          <PlatePhotos order={order} mode={mode} locked={locked} onChanged={onChanged} />
        </>
      )}
    </div>
  );
}

function PlateRows({ rows, items, byId, locked, setRow, onDelete }) {
  const inp = "min-w-0 w-full h-9 bg-slate-800/60 border border-slate-600 text-white rounded-sm px-2 text-sm font-mono-num disabled:opacity-60";
  if (!rows.length) return <div className="text-xs text-red-300" data-testid="plates-empty">Abhi koi cutting size nahi — + ADD SHEET dabaiye.</div>;
  return (
    <div className="space-y-2">
      {rows.map((r, i) => (
        <div key={i} className="grid grid-cols-2 md:grid-cols-[minmax(0,1.6fr)_minmax(0,0.8fr)_minmax(0,0.8fr)_minmax(0,0.7fr)_minmax(0,0.6fr)_minmax(0,0.8fr)_auto] gap-2 items-center surface-2 p-2 rounded-sm" data-testid={`plate-row-${i}`}>
          <select value={r.item_id} disabled={locked} onChange={(e) => setRow(i, "item_id", e.target.value)} className={`${inp} col-span-2 md:col-span-1`} data-testid={`plate-item-${i}`}>
            <option value="">Plate thickness…</option>
            {items.map((m) => <option key={m.id} value={m.id}>{m.name}</option>)}
          </select>
          <input type="number" min="0" placeholder="W inch" value={r.width_in} disabled={locked} onChange={(e) => setRow(i, "width_in", e.target.value)} className={inp} data-testid={`plate-w-${i}`} />
          <input type="number" min="0" placeholder="H inch" value={r.height_in} disabled={locked} onChange={(e) => setRow(i, "height_in", e.target.value)} className={inp} data-testid={`plate-h-${i}`} />
          <label className="flex items-center gap-1 text-[10px] font-mono-num text-slate-400">
            <input type="checkbox" checked={r.round_up} disabled={locked} onChange={(e) => setRow(i, "round_up", e.target.checked)} data-testid={`plate-round-${i}`} /> ROUND-UP
          </label>
          <input type="number" min="1" placeholder="Qty" value={r.quantity} disabled={locked} onChange={(e) => setRow(i, "quantity", e.target.value)} className={inp} data-testid={`plate-qty-${i}`} />
          <div className="font-mono-num text-cyan-300 text-sm" data-testid={`plate-sqft-${i}`}>{n3(rowSqft(r, byId[r.item_id]?.name))} sqft</div>
          {!locked ? (
            <button onClick={() => onDelete(i)} className="text-red-400 hover:text-red-300 p-1 justify-self-end" data-testid={`plate-del-${i}`}><Trash size={16} /></button>
          ) : <span />}
        </div>
      ))}
    </div>
  );
}

export function PlatePhotos({ order, mode, locked, onChanged }) {
  const photos = order.size_photos || [];
  const token = localStorage.getItem("sc_token");
  const fileRef = useRef(null);
  const upload = async (file) => {
    if (!file) return;
    const fd = new FormData();
    fd.append("file", file);
    try {
      await api.post(mode === "fab" ? `/fabrication/orders/${order.id}/size-photos` : `/orders/${order.id}/size-photos`, fd,
        { headers: { "Content-Type": "multipart/form-data" } });
      toast.success("Photo uploaded"); onChanged?.();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-2" data-testid="plates-photos">
      <div className="flex items-center gap-2">
        <span className="text-[10px] font-mono-num tracking-widest text-slate-400">SIZE PHOTOS ({photos.length})</span>
        {!locked && (
          <>
            <button onClick={() => fileRef.current?.click()} className="h-8 px-3 border border-slate-500 text-slate-200 rounded-sm text-[10px] font-mono-num flex items-center gap-1" data-testid="plates-photo-upload-btn"><Camera size={12} /> ADD PHOTO</button>
            <input ref={fileRef} type="file" accept="image/*" capture="environment" className="hidden" onChange={(e) => { upload(e.target.files?.[0]); e.target.value = ""; }} data-testid="plates-photo-input" />
          </>
        )}
      </div>
      {photos.length > 0 && (
        <div className="grid grid-cols-4 md:grid-cols-6 gap-2">
          {photos.map((p) => {
            const url = `${process.env.REACT_APP_BACKEND_URL}/api/orders/${order.id}/size-photos/${p.id}/download?auth=${token}`;
            return (
              <a key={p.id} href={url} target="_blank" rel="noreferrer" className="block aspect-square border border-slate-700 rounded-sm overflow-hidden" data-testid={`plates-photo-${p.id}`}>
                <img src={url} alt={p.original_filename || "size"} className="w-full h-full object-cover" />
              </a>
            );
          })}
        </div>
      )}
    </div>
  );
}
